import { effect } from "@preact/signals";
import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as languageMap from "shared/languageMap";
import BookCollectionStore from "stores/BookCollectionStore";
import SearchQueryStore from "stores/SearchQueryStore";
import stores from "stores/stores";
import { InfiniteScrollLibraryList } from "views/InfiniteScrollLibraryListView";
import LibraryFiltersView from "views/LibraryFiltersView";
import StartHereView from "views/StartHereView";
import WebsitesRowView from "views/WebsitesRowView";

interface AppRouterLike {
  navigate(path: string, options?: { trigger?: boolean }): void;
}

interface PublicLibraryViewProps {
  appRouter: AppRouterLike;
  renderedCallback?: () => void;
  /** How many times its tab's been reselected */
  reselections?: number;
}

interface PublicLibraryState {
  firstFetchCompleted: boolean;
}

export class PublicLibrary extends preact.Component<
  PublicLibraryViewProps,
  PublicLibraryState
> {
  private readonly books = new BookCollectionStore();
  private readonly searchQuery = new SearchQueryStore();
  private readonly list = preact.createRef<InfiniteScrollLibraryList>();
  private disposers: Array<() => void> = [];
  private fetchingBooks = false;

  state: PublicLibraryState = {
    firstFetchCompleted: false,
  };

  componentDidMount() {
    // Search and filters compose: changing one leaves the other intact, so
    // users can search within a filtered subset. Only switching learning
    // language clears the search.
    this.disposers.push(
      this.searchQuery.onQueryChange(() => this.forceUpdate()),
      stores.user.onChange(
        (user) => user.currentLearningLanguage,
        () => this.searchQuery.setQuery("")
      ),
      effect(() => {
        void stores.user.publicBooksCategories.value;
        void stores.user.publicBooksFilter.value;
        void stores.user.currentLearningLanguage.value;
        this.forceUpdate();
      }),
      effect(() => {
        void this.books.books.value;
        this.forceUpdate();
      }),
      this.books.followReadingProgress()
    );
  }

  componentDidUpdate(previousProps: PublicLibraryViewProps) {
    // Its tab reselected: the texts afresh, new ones among them, its search
    // and filters kept. (Not coming back to it, which finds it as left,
    // scrolled down the list.) Shown empty, the list mounts and fetches.
    if (this.props.reselections !== previousProps.reselections) {
      if (this.list.current) this.list.current.resetAndFetch();
      else this.setState({ firstFetchCompleted: false });
    }
  }

  componentWillUnmount() {
    for (const dispose of this.disposers) dispose();
    this.disposers = [];
  }

  markFirstFetchCompleted = () => {
    this.setState({ firstFetchCompleted: true });
    this.props.renderedCallback?.();
  };

  markFetchStateChanged = (isFetching: boolean) => {
    this.fetchingBooks = isFetching;
    this.forceUpdate();
  };

  hasActiveFilters() {
    const filter = stores.user.state.value.publicBooksFilter;
    const categories = stores.user.state.value.publicBooksCategories ?? [];

    return (
      filter?.metrics_CEF !== "A1-C2" ||
      filter?.metrics_words !== "0-Infinity" ||
      filter?.includeAI === false ||
      filter?.likedOnly === true ||
      filter?.videoOnly === true ||
      filter?.includeCompleted === false ||
      categories.filter((category) => category !== "All").length > 0
    );
  }

  renderEmptyPublicLibraryMessage() {
    const languageCode = stores.user.currentLearningLanguage.value;
    const languageString = languageMap.map[languageCode].english;

    return (
      <>
        <p class="noPublicTexts">
          No texts have been shared in {languageString} yet.{" "}
          <a href="/upload">Upload your own texts</a> to read privately or
          become the first contributor to the {languageString} public texts.
        </p>
        {/* The website's way to websites: the Websites page, which explains
            the Web Reader (the iOS app's browser has it built in) */}
        {deviceDetect.isIOSApp() ? null : (
          <p class="websitesLink">
            <a href={`/${languageCode}/links`}>
              Or read any website with the Web Reader ›
            </a>
          </p>
        )}
      </>
    );
  }

  render() {
    const showEmptyPublicLibraryMessage =
      this.state.firstFetchCompleted &&
      !this.fetchingBooks &&
      this.books.length === 0 &&
      this.searchQuery.query.value === "" &&
      !this.hasActiveFilters();

    const language = stores.user.currentLearningLanguage.value;
    return (
      <>
        <StartHereView key={language} />
        {/* The website has no websites row, so its texts need no title */}
        {deviceDetect.isIOSApp() ? (
          <>
            <WebsitesRowView key={language} />
            <div class="discoverSectionHeader">
              <h2>Texts</h2>
            </div>
          </>
        ) : null}
        {showEmptyPublicLibraryMessage ? (
          this.renderEmptyPublicLibraryMessage()
        ) : (
          <>
            <LibraryFiltersView searchQuery={this.searchQuery} />
            <InfiniteScrollLibraryList
              ref={this.list}
              appRouter={this.props.appRouter}
              collection={this.books}
              query={{ public: true }}
              fetchStateChanged={this.markFetchStateChanged}
              rendered={this.markFirstFetchCompleted}
              searchQuery={this.searchQuery}
            />
          </>
        )}
      </>
    );
  }
}
